import {
  createContext, ReactNode, useCallback, useContext, useMemo,
  useState,
} from 'react';
import AdvancedModal from './advancedModal';
import FilterModal from './modalFilter';
import Modal from './modal';
import {
  AdvancedModalConfig,
  FilterModalConfig,
  ModalContextProps,
  ModalState,
  SimpleModalConfig,
} from '../types/modal.type';

export const ModalContext = createContext<ModalContextProps | undefined>(undefined);

export default function ModalProvider({ children }: { children: ReactNode; }) {
  const [modalState, setModalState] = useState<ModalState>(null);

  const closeModal = useCallback(() => {
    setModalState(null);
  }, []);

  const openAdvancedModal = useCallback((config: Omit<AdvancedModalConfig, 'type'>) => {
    const modalConfig: AdvancedModalConfig = {
      ...config,
      type: 'advanced',
      onClose: config.onClose || closeModal,
    };
    setModalState(modalConfig);
  }, [closeModal]);

  const openSimpleModal = useCallback((config: Omit<SimpleModalConfig, 'type'>) => {
    const modalConfig: SimpleModalConfig = {
      ...config,
      type: 'simple',
      onClose: config.onClose || closeModal,
    };
    setModalState(modalConfig);
  }, [closeModal]);

  const openFilterModal = useCallback((config: Omit<FilterModalConfig, 'type'>) => {
    const modalConfig: FilterModalConfig = {
      ...config,
      type: 'filter',
      onClose: config.onClose || closeModal,
    };
    setModalState(modalConfig);
  }, [closeModal]);

  const contextValue: ModalContextProps = useMemo(() => ({
    modalState,
    openAdvancedModal,
    openSimpleModal,
    openFilterModal,
    closeModal,
  }), [modalState, openAdvancedModal, openSimpleModal, openFilterModal, closeModal]);

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
      {modalState?.type === 'advanced' && (
      <AdvancedModal
        isOpen={modalState.isOpen}
        headerIcon={modalState.headerIcon}
        headerText={modalState.headerText}
        actionButton={modalState.actionButton}
        onClose={modalState.onClose}
      >
        {modalState.children}
      </AdvancedModal>
      )}
      {modalState?.type === 'simple' && (
      <Modal
        isOpen={modalState.isOpen}
        onClose={modalState.onClose || closeModal}
        size={modalState.size}
      >
        {modalState.children}
      </Modal>
      )}
      {modalState?.type === 'filter' && (
      <FilterModal
        type={modalState.mode}
        filters={modalState.filters}
        isOpen={modalState.isOpen}
        onClose={modalState.onClose || closeModal}
      />
      )}

    </ModalContext.Provider>
  );
}

export const useModal = (): ModalContextProps => {
  const context = useContext(ModalContext);
  if (context === undefined) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};
export const useAdvancedModal = () => {
  const { openAdvancedModal, closeModal } = useModal();
  return { openAdvancedModal, closeModal };
};

export const useSimpleModal = () => {
  const { openSimpleModal, closeModal } = useModal();
  return { openSimpleModal, closeModal };
};

export const useFilterModal = () => {
  const { openFilterModal, closeModal } = useModal();
  return { openFilterModal, closeModal };
};
